/** @format */

import React, { useEffect, useRef, useState } from "react";
import { awsUrl, scrollTo, triggerGAEvent } from "../helpers";

import { gsap } from "../gsap";
import { ScrollTrigger } from "../gsap/ScrollTrigger";
import { MorphSVGPlugin } from "../gsap/MorphSVGPlugin";
import { useGSAP } from "../gsap/react";

import island from "../assets/bg-island-0229-tinified.jpg";
import sandcastle from "../assets/bg-sandcastle@2x-tinified.jpg";

import { useUserContext } from "../context/UserContext";

gsap.registerPlugin(ScrollTrigger, MorphSVGPlugin);

MorphSVGPlugin.convertToPath("rect, polygon");

const Intro = ({ pageLoaded, isZoom, hashChange, hash }) => {
  const { currentPortal } = useUserContext();

  const container = useRef();
  const headerRef = useRef();
  const header2Ref = useRef();
  const [isAutoplay, setAutoplay] = useState(true);

  const tl = useRef();
  const featuredPinTl = useRef();

  useEffect(() => {
    if (pageLoaded) {
      setAutoplay(true);
      tl.current.timeScale(10).play();
    }
  }, [pageLoaded, setAutoplay, isAutoplay, currentPortal]);

  useEffect(() => {
    if (hashChange || window.scrollY > 200) {
      // introTl.current.kill();
      tl.current.progress(1);

      if (hash === "section-intro") {
        tl.current.kill();
      }
    }
  });

  useEffect(() => {
    if (isZoom) {
      tl.current.progress(1);
    }
  }, [isZoom]);

  useEffect(() => {
    window.addEventListener("resize", ScrollTrigger.refresh());

    return () => {
      window.removeEventListener("resize", ScrollTrigger.refresh());
    };
  });

  useGSAP(
    () => {
      gsap.set(".gradient-backdrop", { opacity: 0 });
      gsap.set("#main-logo2", { autoAlpha: 0 });

      tl.current = gsap
        .timeline({
          onComplete: () => {
            featuredPinTl.current.play();
          },
          immediateRender: false
        })
        .set("#frame-1", { autoAlpha: 1, opacity: 1 })
        .set("#scroll-indicator", { autoAlpha: 1 })
        .set("#pin-featured", { autoAlpha: 0, opacity: 0 })
        .to("#carnival-logo", { autoAlpha: 1, opacity: 1, duration: 2, delay: 0.5 })
        .to("#scroll-indicator", { autoAlpha: 1 }, "<+=1")
        .fromTo(
          "#frame-1",
          { opacity: 1 },
          { opacity: 0, autoAlpha: 0, duration: 15, delay: 20 },
          ">"
        )
        .to("#palms", { autoAlpha: 0, duration: 15, delay: 7 }, "<")
        .to("#scroll-indicator", { autoAlpha: 0 }, "<")
        .fromTo(
          "#header2 > #main-logo2",
          { autoAlpha: 0 },
          { autoAlpha: 1, duration: 10, delay: 5 },
          ">"
        )
        .fromTo(
          "#header2 > h2",
          { scale: 5 },
          { autoAlpha: 1, scale: 1, duration: 10, delay: 2 },
          ">"
        )
        .to("#header2 > p", { autoAlpha: 1, duration: 5 }, ">-=2")
        .to(".gradient-backdrop", { opacity: 1 }, "<");

      featuredPinTl.current = gsap
        .timeline({ paused: true })
        .to("#pin-featured", { autoAlpha: 1, opacity: 1, delay: 0.45 })
        .to("#pin-featured>img", { marginBottom: 0, ease: "elastic.out(1,0.3)" }, "<")
        .to("#pin-featured>div", { width: "175px", opacity: 1, ease: "ease.in" }, "<");
    },
    { scope: container }
  );

  return (
    <section
      className={`relative h-screen min-h-[812px] lg:min-h-[768px]`}
      ref={container}
      id="section-intro"
      data-tag="section_1_header"
    >
      <div
        id="scroll-indicator"
        className="absolute bottom-10 z-[1000] w-full left-0 cursor-pointer h-10 text-center"
      >
        <div className=" text-white mt-2">
          <span
            className="material-symbols-rounded text-3xl lg:text-5xl bounce drop-shadow-md"
            style={{ textShadow: `0px 0px 15px black` }}
          >
            expand_more
          </span>
        </div>
      </div>

      <div id="palms" className="absolute w-full h-full z-50">
        <div
          id="frame-1"
          className="w-full h-full absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2"
        >
          <img
            id="carnival-logo"
            src={awsUrl + "/media/logo-carnival-black.svg"}
            alt="Carnival"
            className="block w-[147px] lg:w-[193px] absolute left-1/2 -translate-x-1/2 top-[5%] lg:top-[6%] -translate-y-1/2 z-[99] "
          />
          <h1
            id="header"
            ref={headerRef}
            aria-label="Introducing a new exclusive destination"
            className="uppercase text-[52px] leading-[52px] lg:text-[62px] lg:leading-auto absolute left-1/2 top-[20%] lg:top-[20%] -translate-x-1/2 -translate-y-1/2 mx-auto font-black  z-50 drop-shadow-xl tracking-normal w-full text-tempo text-[#0E559A]"
            aria-hidden="true"
          >
            <span aria-hidden="true">
              <span className="block text-[35px] lg:text-[41px]">Introducing a</span>
              new exclusive destination
            </span>
          </h1>
        </div>
        <div
          className="bg-cover w-full h-full bg-center"
          style={{ backgroundImage: "url(" + sandcastle + ")" }}
        ></div>
      </div>

      <div id="frame-2" className="absolute w-full h-full">
        <div
          className="block md:hidden w-full h-full "
          style={{
            backgroundImage: "url(" + island + ")",
            backgroundSize: `1600px auto`,
            backgroundPosition: `45% 23%`
          }}
        >
          {" "}
          <div className="absolute h-[100%] lg:h-[50%] bottom-0 bg-gradient-to-t from-dark-blue/85 w-full gradient-backdrop"></div>
        </div>
        <div
          className="hidden md:block bg-cover w-full h-full bg-top"
          style={{ backgroundImage: "url(" + island }}
        >
          <div className="absolute h-[75%] lg:h-[75%] bottom-0 bg-gradient-to-t from-dark-blue/90 w-full gradient-backdrop"></div>
        </div>
      </div>

      <div
        id="header2"
        ref={header2Ref}
        className="absolute left-1/2 top-[23%] lg:top-[23%] w-full  flex items-center flex-col z-[100] max-w-[635px] mx-auto justify-center"
        aria-live="polite"
      >
        <img
          id="main-logo2"
          src={awsUrl + "/media/logo-refined.png?v=1"}
          srcSet={awsUrl + "/media/logo-refined@2x.png?v=1 2x"}
          alt="Celebration Key Carnival "
          className="mb-[108px] lg:mb-36 w-full max-w-[243px] lg:max-w-[242px]"
        />

        <h2
          className="text-tempo text-5xl lg:text-7xl uppercase text-center text-white font-black drop-shadow-2xl tracking-wide"
          aria-label="Your key to paradise"
        >
          <span aria-hidden="true">
            your key to
            <br />
            <span className="text-[85px] lg:text-9xl lg:tracking-wider">paradise</span>
          </span>
        </h2>
        <p className="px-10 lg:px-0 text-white text-[15px] leading-[20px] lg:text-[20px] lg:leading-[27px]">
          With its mile-long, white sand beach, the largest freshwater lagoons in the Caribbean
          <sup className="text-[10px]">*</sup>, and its place in the heart of beautiful Grand
          Bahama, there’s paradise for everyone at Celebration&nbsp;Key<sup>&trade;</sup>.
          <small className="block text-[12px] leading-[16px] mt-3 max-w-[270px] mx-auto lg:max-w-[500px]">
            *Based on publicly available data of surface area measurements as of&nbsp;01/22/24.
          </small>
          <div className=" text-white mt-2">
            <span className="material-symbols-rounded text-3xl bounce">expand_more</span>
          </div>
        </p>
        {currentPortal.key === "retail" && (
          <div
            id="pin-featured"
            role="button"
            onClick={() => {
              triggerGAEvent("section_1_calypso-lagoon");
              scrollTo("featured-portal");
            }}
            className="absolute -bottom-[10%] right-[65%] xl:bottom-[5%] xl:-right-[18%] z-[999] pin-unlocked cursor-pointer"
          >
            {" "}
            <img
              src={`${awsUrl}/media/portals/adult-friendly/pin.png`}
              srcSet={`${awsUrl}/media/portals/adult-friendly/pin@2x.png`}
              alt="flamingo pin - view the featured portal"
              className=" w-[75px]"
            />
            <div className="bg-white/75 rounded-xl absolute top-1/2 -translate-y-1/2 uppercase text-[13px] text-dark-blue text-left">
              <span>
                <span className="font-bold block">Just unlocked:</span> adult-friendly portal&nbsp;»
              </span>
            </div>
          </div>
        )}

        {currentPortal.key === "family-friendly" && (
          <div
            id="pin-featured"
            role="button"
            onClick={() => {
              triggerGAEvent("section_portals");
              scrollTo("featured-portal");
            }}
            className="absolute -bottom-[11%] right-[60%] xl:bottom-[5%] xl:-right-[18%] z-[999] pin-unlocked cursor-pointer"
          >
            {" "}
            <img
              src={`${awsUrl}/media/portals/retail/pin.png`}
              srcSet={`${awsUrl}/media/portals/retail/pin@2x.png`}
              alt="view the featured portal"
              className=" w-[75px]"
            />
            <div className="bg-white/75 rounded-xl absolute top-1/2 -translate-y-1/2 uppercase text-[13px] text-dark-blue text-left retail-pin">
              <span>
                <span className="font-bold block">Just unlocked:</span> Retail Portal&nbsp;»
              </span>
            </div>
          </div>
        )}

        {currentPortal.key === "private-club" && (
          <div
            id="pin-featured"
            role="button"
            onClick={() => {
              triggerGAEvent("section_portals");
              scrollTo("featured-portal");
            }}
            className="absolute -bottom-[13%] right-[60%] xl:bottom-[5%] xl:-right-[18%] z-[999] pin-unlocked cursor-pointer"
          >
            {" "}
            <img
              src={`${awsUrl}/media/portals/family-friendly/pin.png`}
              srcSet={`${awsUrl}/media/portals/family-friendly/pin@2x.png`}
              alt="view the featured portal"
              className=" w-[75px]"
            />
            <div className="bg-white/75 rounded-xl absolute top-1/2 -translate-y-1/2 uppercase text-[13px] text-dark-blue text-left family-friendly-pin">
              <span>
                <span className="font-bold block">Just unlocked:</span> Family-Friendly
                Portal&nbsp;»
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Intro;
